import {
  ChangeDetectorRef,
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnInit,
  OnDestroy,
  Output,
  AfterViewChecked,
} from '@angular/core';
import { AlertController } from '@ionic/angular';
import { Observable, of, SubscriptionLike } from 'rxjs';

import {
  CalendarDayViewAvailability,
  CalendarAvailabilities,
  CalendarEvent,
  DayViewConfig,
  TimeSlotEvent,
  SMDragEndEvent,
} from '../day-view-calendar/day-view-calendar.component';
import { SalonService } from '../../../services/salon.service';
import { CalendarService } from '../../../services/calendar.service';
import { Stylist } from '../../../models';
import { Utilities } from '../../../utilities/utilities';
import { List, Map } from 'immutable';
import { ENV_CONFIG } from '../../../../bin/env.config';

@Component({
  selector: 'smr-all-stylist-view-calendar',
  templateUrl: './all-stylist-view-calendar.component.html',
  styleUrls: ['./all-stylist-view-calendar.component.scss'],
})
export class AllStylistViewCalendarComponent
  implements OnInit, OnChanges, OnDestroy, AfterViewChecked {
  @Input() currentDate: Date;

  @Input() stylistID: number;

  @Input() muteEventTappability: boolean;

  @Output() onTimeSelected = new EventEmitter<TimeSlotEvent>();

  @Output() onEventSelected = new EventEmitter<CalendarEvent>();

  @Output() onRangeChanged = new EventEmitter<List<DayViewConfig>>();

  @Output() eventsBeforeUpdate = new EventEmitter<boolean>();

  @Output() eventsDidUpdate = new EventEmitter<boolean>();

  @Output() onError = new EventEmitter<any>();

  @Output() onStylistHeaderSelected = new EventEmitter<Stylist>();

  @Output() onEventDragEnd = new EventEmitter<SMDragEndEvent>();

  @HostListener('window:orientationchange', ['$event'])
  orientationChangedHandler(event) {
    if (this.init) {
      this.refresh();
    }
  }

  public numOfViews: number;

  public views: List<DayViewConfig>;

  public stylists: List<Stylist>;

  public allStaffAvailabilities: Map<string, CalendarAvailabilities>;

  public availabilitiesCacheYear: string;

  public availabilitiesCacheMonth: string;

  public init: boolean;

  public calendarMonitor$: SubscriptionLike;

  public env_config: any = ENV_CONFIG;


  public deviceOrientationChanged: boolean;

  constructor(
    private changeDetectorRef: ChangeDetectorRef,
    private calendarService: CalendarService,
    private salonService: SalonService,
    private alertController: AlertController
  ) {
    this.views = List.of<DayViewConfig>();
    this.allStaffAvailabilities = Map();
    this.numOfViews = 0;
  }

  public ngOnInit() {
    this.init = true;
    this.currentDate = this.currentDate || new Date();
    this.refresh();
    this.calendarMonitor$ = this.calendarService
      .getCalendarUpdateMonitor()
      .subscribe((update) => {
        if (update) {
          this.refresh();
        }
      });
  }

  public ngAfterViewChecked() {
    this.matchWidths();
    this.matchHeights();
  }

  public ngOnChanges() {
    if (this.init) {
      this.refresh();
    }
  }

  public ngOnDestroy() {
    this.calendarMonitor$.unsubscribe();
  }

  public refresh() {
    this.eventsBeforeUpdate.emit(true);
    let tempViews = List.of<DayViewConfig>();
    // this.viewsWidth = List([]);
    // this.allStaffAvailabilities = Map.of();
    this.salonService.getAllStaff(this.currentDate).subscribe({
      next: (stylists: List<Stylist>) => {
        this.stylists = stylists
          .filter((stylist) => stylist.active == 1)
          .sort((a, b) => {
            return a.sort - b.sort;
          });
        this.numOfViews = this.stylists.count();

        this.fetchAvailabilities().subscribe({
          next: (availabilities) => {
            let index: number = 0;
            this.stylists.forEach((stylist: Stylist) => {
              //todo let dayViewAvailabilitiy: ICalendarDayViewAvailability;
              let dayViewAvailabilitiy: any;
              const day: string = Utilities.formatDate(this.currentDate, 'D');
              const hideHourCol: boolean = index % 2 === 1;

              if (
                availabilities.has(stylist.id + '') &&
                availabilities.get(stylist.id + '').has(day)
              ) {
                dayViewAvailabilitiy = availabilities.getIn([
                  stylist.id + '',
                  day,
                ]);
              }

              // calculate each view's width
              const hourColWidth: number = 45;
              const minuteColWidth: number = 15;
              // const numOfViewsWithHourCol: number = Math.ceil(this.numOfViews / 2);
              // const screenSizeToDivide: number = this.platform.width() - (numOfViewsWithHourCol * (hourColWidth + (minuteColWidth * 2))) + minuteColWidth;
              const viewSize = 175;
              let width: number = hideHourCol
                ? viewSize
                : viewSize + hourColWidth + minuteColWidth * 2;

              if (index === 0) {
                width = width - minuteColWidth;
              }

              // this.viewsWidth = this.viewsWidth.push(width + 'px');

              tempViews = tempViews.push(
                new DayViewConfig({
                  date: this.currentDate,
                  events: this.calendarService.groupBookings(
                    stylist,
                    stylist.bookings
                  ),
                  availabilities: dayViewAvailabilitiy,
                  stylist: stylist,
                  hideTimeSlotLabels: hideHourCol,
                  showLeftMinuteLabels: index > 0,
                  width: width + 'px',
                  muteEventTappability: this.muteEventTappability,
                })
              );

              index++;
            });

            this.views = tempViews;
            this.onRangeChanged.emit(this.views);
            this.eventsDidUpdate.emit(true);
            this.changeDetectorRef.markForCheck();
          },
          error: (error) => {
            this.onError.emit(error);
          },
        });
      },
      error: (error) => {
        this.onError.emit(error);
      },
    });
  }

  public eventSelectedHandler(event) {
    this.onEventSelected.emit(event);
  }

  public onTimeSelectedHandler(event: TimeSlotEvent) {
    this.onTimeSelected.emit(event);
  }

  public fetchAvailabilities(): Observable<
    Map<string, CalendarAvailabilities>
  > {
    const year: string = Utilities.formatDate(this.currentDate, 'Y');
    const month: string = Utilities.formatDate(this.currentDate, 'MM');

    if (
      this.availabilitiesCacheMonth !== month ||
      this.availabilitiesCacheYear !== year
    ) {
      return new Observable<Map<string, CalendarAvailabilities>>((observer) => {
        this.salonService.getAllStaffAvailabilities(year, month).subscribe({
          next: (result) => {
            // update the cache
            this.availabilitiesCacheYear = year;
            this.availabilitiesCacheMonth = month;
            this.allStaffAvailabilities = result;

            observer.next(this.allStaffAvailabilities);
            observer.complete();
          },
          error: (error) => {
            observer.error(error);
            observer.complete();
          },
        });
      });
    } else {
      return of(this.allStaffAvailabilities);
    }
  }

  public onStylistTap(stylist: Stylist) {
    this.onStylistHeaderSelected.emit(stylist);
  }

  public onEventDragEndHandler($event) {
    this.onEventDragEnd.emit($event);
  }

  public trackByFn(index, item) {
    //src: https://github.com/angular/angular/issues/10182
    // src: https://netbasal.com/angular-2-improve-performance-with-trackby-cc147b5104e5
    return index; // or item.id
  }

  public matchWidths() {
    const leftPanel = document.getElementsByClassName(
      'split-pane-side'
    ) as HTMLCollectionOf<HTMLElement>;
    const calBody = document.getElementsByClassName(
      'smr-stylist-calendar-container'
    ) as HTMLCollectionOf<HTMLElement>;
    if (calBody[0] && leftPanel[0]) {
      calBody[0].style.width = `calc(100vw - ${leftPanel[0].scrollWidth}px)`;
    }

    const calHeader = document.getElementsByClassName('smr-stylist-calendar-header') as HTMLCollectionOf<HTMLElement>;
    var num = this.views.count();
    const calendarTable = document.getElementsByClassName('sm-scroll-item') as HTMLCollectionOf<HTMLElement>;
    var oneColWidth = 175
    if (calendarTable[0]) {
      oneColWidth = calendarTable[0].scrollWidth - 61
    }
    var headerWidth = num * oneColWidth + Math.floor(num / 2) * 77 + 61;
    if (calHeader[0]) {
      calHeader[0].style.minWidth = `${headerWidth}px`;
    }
  }

  public matchHeights() {
    const calHeader = document.getElementsByClassName(
      'smr-calendar-header'
    ) as HTMLCollectionOf<HTMLElement>;
    const calBody = document.getElementsByClassName(
      'smr-stylist-calendar-container'
    ) as HTMLCollectionOf<HTMLElement>;
    calBody[0].style.height = `calc(100vh - ${calHeader[0].scrollHeight}px)`;
  }
}
